<template>
    <div>
        <template v-if="type === 'employee'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" :alt="result.name" class="employee-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.name"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.function" class="result-function" v-html="result.function"></p>
                    <p v-if="result.email" class="result-email">{{ result.email }}</p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'headline'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="article-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.sub_title" class="result-subtitle" v-html="result.sub_title"></p>
                    <div class="result-meta">
                        <p v-if="result.online_date" class="date">
                            {{ currentLanguage === 'en' ? 'Published:' : 'Gepubliceerd:' }} {{ result.online_date }}
                        </p>
                    </div>
                    <p class="result-excerpt" v-html="result.summary || result.body"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'solution'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="default-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                        <span v-if="result.is_greencare === 1" class="greencare-badge">GreenCare</span>
                    </div>
                    <p class="result-excerpt" v-html="result.summary || result.body"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'product'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="product-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 v-if="result.material" class="result-title" v-html="result.title.concat(' - ', result.material)"></h4>
                        <h4 v-else class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>

                    <!-- Product Options -->
                    <div v-if="result.has_obese_option || result.has_circular_option" class="product-options">
                        <span v-if="result.has_obese_option" class="option-badge obese-option me-1">
                            obese
                        </span>
                        <span v-if="result.has_circular_option" class="option-badge circular-option">
                            {{ currentLanguage === 'en' ? 'circular' : 'circulair' }}
                        </span>
                    </div>

                    <p class="result-excerpt" v-html="result.summary || result.body"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'product-group'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="product-group-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.brand_title || result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.long_title && result.long_title !== result.title" class="result-subtitle" v-html="result.long_title"></p>
                    <p class="result-excerpt" v-html="result.summary"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'product-variation'">
            <a :href="result.product_url" class="result-link" tabindex="-1">
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.product_title" class="result-product">
                        {{ currentLanguage === 'en' ? 'From:' : 'Van:' }} {{ result.product_title }}
                    </p>
                    <p v-if="result.article_code" class="result-article-code">
                        {{ currentLanguage === 'en' ? 'Article number:' : 'Artikelnummer:' }} {{ result.article_code }}
                    </p>
                    <div class="result-specs">
                        <span v-if="result.size" class="spec-badge">{{ result.size }}</span>
                        <span v-if="result.sling_size" class="spec-badge">{{ result.sling_size }}</span>
                        <span v-if="result.sling_type" class="spec-badge">{{ result.sling_type }}</span>
                        <span v-if="result.lift_type" class="spec-badge">{{ result.lift_type }}</span>
                    </div>
                    <p v-if="result.description" class="result-excerpt" v-html="result.description"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'product-accessory'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="accessory-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.article_code" class="result-article-code">
                        {{ currentLanguage === 'en' ? 'Article number:' : 'Artikelnummer:' }} {{ result.article_code }}
                    </p>
                    <p class="result-excerpt" v-html="result.body"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'concept'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="concept-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.long_title && result.long_title !== result.title" class="result-subtitle" v-html="result.long_title"></p>
                    <p class="result-excerpt" v-html="result.summary"></p>
                </div>
            </a>
        </template>

        <template v-else-if="type === 'vacancy'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="vacancy-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.long_title && result.long_title !== result.title" class="result-subtitle" v-html="result.long_title"></p>
                    <p class="result-excerpt" v-html="result.summary || result.body"></p>
                </div>
            </a>
        </template>

        <!-- Page Result -->
        <template v-else-if="type === 'page'">
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="page-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p v-if="result.long_title && result.long_title !== result.title" class="result-subtitle" v-html="result.long_title"></p>
                    <p class="result-excerpt" v-html="result.summary || result.body"></p>
                </div>
            </a>
        </template>

        <!-- Default Result -->
        <template v-else>
            <a :href="result.url" class="result-link" tabindex="-1">
                <div v-if="result.image" class="result-image">
                    <img :src="result.image" alt="" class="default-image">
                </div>
                <div class="result-content">
                    <div class="result-header">
                        <h4 class="result-title" v-html="result.title"></h4>
                        <span class="result-badge">{{ getLocalizedBadge(result.index_singular) }}</span>
                    </div>
                    <p class="result-excerpt" v-html="result.summary || result.body"></p>
                </div>
            </a>
        </template>
    </div>
</template>

<script setup>
import {computed} from 'vue'

const props = defineProps({
    result:          {
        type:     Object,
        required: true
    },
    currentLanguage: {
        type:    String,
        default: 'nl'
    }
})

const type = computed(() => {
    const singularKey = props.result.index_singular?.toLowerCase()

    switch(singularKey) {
        case 'medewerker':
        case 'employee':
            return 'employee'
        case 'nieuwsbericht':
        case 'headline':
        case 'news':
            return 'headline'
        case 'oplossing':
        case 'solution':
            return 'solution'
        case 'product':
            return 'product'
        case 'productgroep':
        case 'product group':
            return 'product-group'
        case 'productvariatie':
        case 'product variation':
            return 'product-variation'
        case 'accessoire':
        case 'accessory':
            return 'product-accessory'
        case 'concept':
            return 'concept'
        case 'vacature':
        case 'vacancy':
            return 'vacancy'
        case 'stap':
        case 'step':
            return 'total-approach-step'
        case 'pagina':
        case 'page':
            return 'page'
        default:
            return 'default'
    }
})

const getLocalizedBadge = (badge) => {
    if(!badge) return ''

    // Normalize the badge to lowercase for consistent lookup
    const normalizedBadge = badge.toLowerCase()

    // Badge translations with proper capitalization
    const translations = {
        'nl': {
            'employee':          'Medewerker',
            'medewerker':        'Medewerker',
            'headline':          'Nieuwsbericht',
            'nieuwsbericht':     'Nieuwsbericht',
            'news':              'Nieuwsbericht',
            'solution':          'Oplossing',
            'oplossing':         'Oplossing',
            'product':           'Product',
            'product group':     'Productgroep',
            'productgroep':      'Productgroep',
            'product variation': 'Productvariatie',
            'productvariatie':   'Productvariatie',
            'accessory':         'Accessoire',
            'accessoire':        'Accessoire',
            'concept':           'Concept',
            'vacancy':           'Vacature',
            'vacature':          'Vacature',
            'step':              'Stap',
            'stap':              'Stap',
            'page':              'Pagina',
            'pagina':            'Pagina'
        },
        'en': {
            'medewerker':        'Employee',
            'employee':          'Employee',
            'nieuwsbericht':     'News',
            'headline':          'News',
            'news':              'News',
            'oplossing':         'Solution',
            'solution':          'Solution',
            'product':           'Product',
            'productgroep':      'Product Group',
            'product group':     'Product Group',
            'productvariatie':   'Product Variation',
            'product variation': 'Product Variation',
            'accessoire':        'Accessory',
            'accessory':         'Accessory',
            'concept':           'Concept',
            'vacature':          'Vacancy',
            'vacancy':           'Vacancy',
            'stap':              'Step',
            'step':              'Step',
            'pagina':            'Page',
            'page':              'Page'
        }
    }

    // Get the translation or fallback to properly capitalized original
    const translation = translations[props.currentLanguage]?.[normalizedBadge]

    if(translation) {
        return translation
    }

    // Fallback: capitalize first letter of the original badge
    return badge.charAt(0).toUpperCase() + badge.slice(1).toLowerCase()
}
</script>

